import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  CssBaseline, 
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
  useMediaQuery
} from '@mui/material';
import { 
  Search as SearchIcon, 
  Dashboard as DashboardIcon,
  Analytics as AnalyticsIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import SimpleScanForm from './components/scan/SimpleScanForm';
import CardResultsView from './components/results/CardResultsView';
import SimpleAnalyticsView from './components/results/SimpleAnalyticsView';
import { ThemeProvider, useThemeMode } from './theme/ThemeProvider';
import Header from './components/common/Header';
import { motion } from 'framer-motion';
import './components/common/ButtonStyles.css';
// Logo paths from public directory
const logoPathSidebar = '/images/Ace rep-02.png';
const logoPathFooter = '/images/Ace rep-03.png';

// Content panel component
function ContentPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <motion.div
      role="tabpanel"
      hidden={value !== index}
      id={`content-panel-${index}`}
      aria-labelledby={`nav-item-${index}`}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: value === index ? 1 : 0, x: value === index ? 0 : 20 }}
      transition={{ duration: 0.3 }}
      style={{ display: value === index ? 'block' : 'none', width: '100%' }}
      {...other}
    >
      {value === index && children}
    </motion.div>
  );
}

function AppContent() {
  const { mode } = useThemeMode();
  const [activeView, setActiveView] = useState(0);
  const [scanResults, setScanResults] = useState(null);
  const [creatorName, setCreatorName] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isMobile = useMediaQuery('(max-width:900px)');
  const drawerWidth = 240;

  const handleNavChange = (newValue) => {
    setActiveView(newValue);
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  const handleScanComplete = (results) => {
    setScanResults(results);
    setCreatorName(results.creatorName);
    setActiveView(1); // Switch to Results view
  };

  const handleExport = (data, format) => {
    console.log(`Exporting ${data.length} results as ${format}`);
    // In a real app, this would generate and download a file
    alert(`Exported ${data.length} results as ${format}`);
  };
  
  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  // Navigation items
  const navItems = [
    { text: 'Scan', icon: <SearchIcon />, disabled: false },
    { text: 'Results', icon: <DashboardIcon />, disabled: !scanResults },
    { text: 'Analytics', icon: <AnalyticsIcon />, disabled: !scanResults },
    { text: 'Settings', icon: <SettingsIcon />, disabled: false }
  ];

  // Drawer content
  const drawer = (
    <>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        p: 2,
        borderBottom: 1,
        borderColor: 'divider'
      }}>
        <img 
          src={logoPathFooter} 
          alt="Ace Reputations Logo" 
          style={{ height: '50px', width: 'auto' }} 
        />
      </Box>
      <List sx={{ mt: 2 }}>
        {navItems.map((item, index) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton 
              onClick={() => handleNavChange(index)}
              selected={activeView === index}
              disabled={item.disabled}
              sx={{
                borderRadius: '0 20px 20px 0',
                mx: 1,
                '&.Mui-selected': {
                  background: mode === 'dark' 
                    ? 'linear-gradient(90deg, rgba(0,0,255,0.2) 0%, rgba(0,191,255,0.1) 100%)'
                    : 'linear-gradient(90deg, rgba(0,0,255,0.1) 0%, rgba(0,191,255,0.05) 100%)',
                  borderLeft: '3px solid',
                  borderColor: 'primary.main'
                }
              }}
            >
              <ListItemIcon sx={{ color: activeView === index ? 'primary.main' : 'inherit' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </>
  );

  return (
    <Box sx={{ 
      display: 'flex',
      minHeight: '100vh',
      backgroundColor: 'background.default'
    }}>
      {/* Sidebar for desktop */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              border: 'none',
              boxShadow: mode === 'light' ? '0px 0px 10px rgba(0,0,0,0.05)' : 'none'
            },
          }}
        >
          {drawer}
        </Drawer>
      )}

      {/* Mobile drawer */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={drawerOpen}
          onClose={handleDrawerToggle}
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
            },
          }}
        >
          {drawer}
        </Drawer>
      )}

      {/* Main content */}
      <Box sx={{ 
        flexGrow: 1, 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        <Header onMenuToggle={isMobile ? handleDrawerToggle : undefined} />

        <Box component="main" sx={{ 
          flexGrow: 1, 
          p: 3,
          overflow: 'auto'
        }}>
          <Container maxWidth="xl">
            <Paper 
              elevation={0} 
              sx={{ 
                p: { xs: 2, md: 3 }, 
                borderRadius: 2,
                mb: 3,
                border: '1px solid',
                borderColor: mode === 'light' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.2)',
                background: mode === 'light' 
                  ? 'rgba(255, 255, 255, 0.8)' 
                  : 'rgba(30, 30, 40, 0.75)',
                backdropFilter: 'blur(10px)',
                boxShadow: mode === 'light' 
                  ? '0px 0px 20px rgba(112, 71, 220, 0.1)' 
                  : '0px 0px 20px rgba(0, 0, 0, 0.3)'
              }}
            >
              <ContentPanel value={activeView} index={0}>
                <SimpleScanForm onScanComplete={handleScanComplete} />
              </ContentPanel>
              
              <ContentPanel value={activeView} index={1}>
                {scanResults && (
                  <CardResultsView 
                    creatorName={creatorName} 
                    scanResults={scanResults}
                    onExport={handleExport}
                  />
                )}
              </ContentPanel>
              
              <ContentPanel value={activeView} index={2}>
                {scanResults && (
                  <SimpleAnalyticsView 
                    creatorName={creatorName}
                    scanResults={scanResults}
                  />
                )}
              </ContentPanel>

              <ContentPanel value={activeView} index={3}>
                <Box sx={{ p: 2 }}>
                  <h2>Settings</h2>
                  <p>Application settings will appear here.</p>
                </Box>
              </ContentPanel>
            </Paper>
          </Container>
        </Box>

        {/* Footer */}
        <Box 
          component="footer" 
          sx={{ 
            p: 3, 
            borderTop: 1,
            borderColor: 'divider',
            background: mode === 'dark' 
              ? 'linear-gradient(to bottom, #020219, #000113)'
              : 'linear-gradient(to bottom, #f8f9fa, #e3d9fb 50%)',
          }}
        >
          <Container maxWidth="xl">
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3
            }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: { xs: 2, md: 0 }
              }}>
                <img 
                  src={logoPathSidebar} 
                  alt="Ace Reputations Logo" 
                  style={{ height: '60px', width: 'auto' }} 
                />
              </Box>
              
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2 
              }}>
                <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                  <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                    Tools
                  </Typography>
                  <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                    <Box component="li" sx={{ mb: 0.5 }}>
                      <Typography variant="body2">Sentiment Tracker</Typography>
                    </Box>
                    <Box component="li" sx={{ mb: 0.5 }}>
                      <Typography variant="body2">Domain Removal Detection</Typography>
                    </Box>
                    <Box component="li" sx={{ mb: 0.5 }}>
                      <Typography variant="body2">Auto-De-Indexing submission</Typography>
                    </Box>
                  </Box>
                </Box>
                
                <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                  <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                    Contact
                  </Typography>
                  <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                    <Box component="li" sx={{ mb: 0.5 }}>
                      <Typography variant="body2">support@acereputations.com</Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
            
            <Divider sx={{ mb: 2 }} />
            
            <Typography variant="body2" align="center" sx={{ opacity: 0.7 }}>
              © {new Date().getFullYear()} Ace Reputations - All Rights Reserved
            </Typography>
          </Container>
        </Box>
      </Box>
    </Box>
  );
}

function SimpleApp() {
  return (
    <ThemeProvider>
      <CssBaseline />
      <AppContent />
    </ThemeProvider>
  );
}

export default SimpleApp;
